import { useApplicationContext } from "contexts/ApplicationContext";
import { Controls3cPlusActions } from "contexts/types/context-3cplus-types";
import { jssipManager } from "./jssip-manager";
import { get3CplusAuthenticatedUser } from "services/3cplus/requests";
import { RTCSession } from "jssip/lib/RTCSession";
import { Toast } from "components/toast";

export let jssipSession: any = null;

let localStream: MediaStream | null = null;

export const useSipServiceConection = ({
  onAuthenticatedUserError,
  onMicrophonePermissionDenied,
  onConnected,
  onDisconnected,
  dispatch3cPlus,
  onLigacaoChanged,
  onRamalChanged,
  getAudioElement,
}: {
  onMicrophonePermissionDenied?: () => void;
  onAuthenticatedUserError?: (error: Error) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onLigacaoChanged?: (emLigacao: boolean) => void;
  dispatch3cPlus: React.Dispatch<Controls3cPlusActions>;
  onRamalChanged: (mutted: boolean) => void;
  getAudioElement: () => HTMLAudioElement | null;
}) => {
  const { user } = useApplicationContext();

  // Se 3cplus não está habilitado, retorna helpers vazios
  if (!user.userData.enabled3cplus) {
    return { deslogarAgenteSipService: () => {} };
  }

  // --- Helpers locais organizados ---
  const setupMediaPermissions = async () => {
    try {
      // captura e guarda o stream local
      localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log("Permission is granted!");

      return localStream;
    } catch (err) {
      console.warn("Microphone permission denied");
      if (onMicrophonePermissionDenied) onMicrophonePermissionDenied();
      localStream = null;
      return null;
    }
  };

  const attachSessionHandlers = (session: RTCSession) => {
    session.on("accepted", () => {
      console.log("accepted");
      onLigacaoChanged?.(true);
    });

    session.on(
      "peerconnection",
      (data: { peerconnection: RTCPeerConnection }) => {
        const pc = data.peerconnection;

        pc.addEventListener("addstream", (ev: any) => {
          console.log("RECEBIDO STREAM AUDIO");
          const audioElement = getAudioElement();
          if (audioElement) {
            audioElement.srcObject = ev.stream;
            audioElement.play().catch((e) => {
              console.warn("Erro ao reproduzir áudio recebido", e);
            });
          }
        });
      }
    );

    session.on("getusermediafailed", () => {
      console.warn("getUserMedia failed for session");
      if (onMicrophonePermissionDenied) onMicrophonePermissionDenied();
      Toast({ title: "Permissão de microfone negada", status: "error" });
    });

    session.on("ended", () => {
      console.log("ended");
      onLigacaoChanged?.(false);
    });

    session.on("muted", () => {
      console.log("muted");
      dispatch3cPlus({ type: "mute" });
      onRamalChanged(true);
    });

    session.on("unmuted", () => {
      console.log("unmuted");
      dispatch3cPlus({ type: "unMute" });
      onRamalChanged(false);
    });

    // garante resposta automática para sessões recebidas
    try {
      session.answer({
        mediaConstraints: {
          audio: { deviceId: { exact: "default" } },
          video: false,
        },
      });
      console.log("Sessão SIP respondida automaticamente");
    } catch (e) {
      // ignore
      console.error("Erro ao responder sessão SIP automaticamente", e);
    }
  };

  // UA event handling is delegated to jssipManager; session handlers are attached when newRTCSession fires

  // inicia conexão JSSIP (executa uma vez)
  (async () => {
    if (jssipManager.getUA()) return;

    try {
      const response = await get3CplusAuthenticatedUser();
      const dominio_3cplus = "3c.fluxoti.com";
      const dominio_da_empresa = response.company.domain;
      const host = `${dominio_da_empresa}.${dominio_3cplus}:4443`;
      const usuario_do_ramal = response.telephony_id;
      const senha_do_ramal = response.extension_password;

      // inicializa manager (idempotente)
      await jssipManager.initFromAuth({
        host,
        user: usuario_do_ramal,
        password: senha_do_ramal,
      });
      // permissões de áudio
      await setupMediaPermissions();

      // registramos handlers garantindo que não tenhamos duplicatas: removemos handlers
      // anteriores e registramos os nossos.
      jssipManager.off("newRTCSession");
      jssipManager.off("connected");
      jssipManager.off("disconnected");

      jssipManager.on("newRTCSession", (data: any) => {
        jssipSession = data.session;
        attachSessionHandlers(data.session);
        console.log("newRTCSession received and handlers attached");
      });

      jssipManager.on("connected", () => {
        onConnected?.();
      });

      jssipManager.on("disconnected", (e: any) => {
        onDisconnected?.();
      });
    } catch (e) {
      console.error(
        "Erro ao buscar informações do usuário autenticado 3cplus",
        e
      );
      if (onAuthenticatedUserError) onAuthenticatedUserError(e as Error);
    }
  })();

  const deslogarAgenteSipService = () => {
    console.log("Desconectando ramal Sip..");
    try {
      jssipManager.stop();
    } catch (err) {
      console.warn("Erro ao desconectar jssip", err);
    } finally {
      // UA lifecycle is managed by jssipManager
    }
  };

  return { deslogarAgenteSipService };
};
